/**
 * Réglages des offres (migration 0717) — lus par `get_offer_settings()` :
 * durées de pass proposées, essai gratuit des abonnements mensuels, promo
 * SKILLCARDS en cours. Helpers PURS partagés par l'app et le site.
 *
 * ⚠️ Le serveur décide (checkouts, `skillcard_quote`) ; ces helpers ne font
 * qu'afficher ce qu'il appliquera.
 */

export const PASS_DURATIONS = ['24h', '7d', '30d'] as const;
export type OfferPassDuration = (typeof PASS_DURATIONS)[number];

export interface SkillcardPromo {
  id?: string;
  percent: number;
  label: string;
  starts_at?: string;
  ends_at: string;
}

export interface OfferSettings {
  passes: Record<OfferPassDuration, boolean>;
  trial: { enabled: boolean; days: number; eligible: boolean };
  promo: SkillcardPromo | null;
}

/** Valeurs quand le serveur ne répond pas : tout proposé, pas d'essai ni de promo. */
export const DEFAULT_OFFER_SETTINGS: OfferSettings = {
  passes: { '24h': true, '7d': true, '30d': true },
  trial: { enabled: false, days: 7, eligible: false },
  promo: null,
};

/** Réponse de `get_offer_settings` relue sans jamais faire planter l'affichage. */
export function parseOfferSettings(raw: unknown): OfferSettings {
  if (!raw || typeof raw !== 'object') return DEFAULT_OFFER_SETTINGS;
  const r = raw as Record<string, unknown>;
  const p = (r.passes ?? {}) as Record<string, unknown>;
  const t = (r.trial ?? {}) as Record<string, unknown>;
  const promo = r.promo as Record<string, unknown> | null | undefined;
  const days = Number(t.days);
  return {
    passes: { '24h': p['24h'] !== false, '7d': p['7d'] !== false, '30d': true },
    trial: {
      enabled: t.enabled === true,
      days: Number.isInteger(days) && days >= 1 && days <= 30 ? days : 7,
      eligible: t.eligible === true,
    },
    promo:
      promo &&
      typeof promo === 'object' &&
      Number.isFinite(Number(promo.percent)) &&
      typeof promo.ends_at === 'string'
        ? {
            id: typeof promo.id === 'string' ? promo.id : undefined,
            percent: Number(promo.percent),
            label: typeof promo.label === 'string' ? promo.label : '',
            starts_at: typeof promo.starts_at === 'string' ? promo.starts_at : undefined,
            ends_at: promo.ends_at,
          }
        : null,
  };
}

/** Durées proposées, dans l'ordre (30 jours toujours). */
export function availablePassDurations(settings: OfferSettings): OfferPassDuration[] {
  return PASS_DURATIONS.filter((d) => settings.passes[d]);
}

/** Durée sélectionnée par défaut : la préférée si elle est proposée, sinon 30 jours. */
export function defaultPassDuration(
  settings: OfferSettings,
  preferred: OfferPassDuration = '7d',
): OfferPassDuration {
  return settings.passes[preferred] ? preferred : '30d';
}

/** Durée encore valable après un changement de réglage. */
export function keepPassDuration(
  settings: OfferSettings,
  current: OfferPassDuration,
): OfferPassDuration {
  return settings.passes[current] ? current : defaultPassDuration(settings);
}

/** Essai gratuit affiché pour un abonnement mensuel (null = pas d'essai). */
export function trialDays(settings: OfferSettings): number | null {
  return settings.trial.enabled && settings.trial.eligible ? settings.trial.days : null;
}

/** « 7 jours gratuits, puis 9,99 €/mois ». */
export function trialLine(days: number, priceLabel: string): string {
  return `${days} jour${days > 1 ? 's' : ''} gratuit${days > 1 ? 's' : ''}, puis ${priceLabel}`;
}

/** Promo en cours à un instant (bornes revérifiées côté client). */
export function activePromo(
  settings: OfferSettings,
  now: Date = new Date(),
): SkillcardPromo | null {
  const p = settings.promo;
  if (!p) return null;
  const end = Date.parse(p.ends_at);
  if (!Number.isFinite(end) || end <= now.getTime()) return null;
  if (p.starts_at && Date.parse(p.starts_at) > now.getTime()) return null;
  return p;
}

/** Temps restant d'une promo : « 2 j 4 h », « 3 h 12 min », « 8 min ». */
export function promoTimeLeft(endsAt: string, now: Date = new Date()): string | null {
  const ms = Date.parse(endsAt) - now.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const min = Math.floor(ms / 60_000);
  const days = Math.floor(min / 1440);
  const hours = Math.floor((min % 1440) / 60);
  const mins = min % 60;
  if (days > 0) return hours > 0 ? `${days} j ${hours} h` : `${days} j`;
  if (hours > 0) return mins > 0 ? `${hours} h ${mins} min` : `${hours} h`;
  return `${Math.max(1, mins)} min`;
}

/** « jusqu'au 15 novembre » (heure de Paris). */
export function promoEndLabel(endsAt: string): string {
  const d = new Date(endsAt);
  if (!Number.isFinite(d.getTime())) return '';
  const day = d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Paris',
  });
  return `jusqu’au ${day}`;
}

/** Titre de la promo : son nom, sinon « −20 % sur toutes les SKILLCARDS ». */
export function promoHeadline(promo: SkillcardPromo): string {
  const pct = `−${promo.percent}\u00a0%`;
  return promo.label.trim()
    ? `${promo.label.trim()} · ${pct} sur toutes les SKILLCARDS`
    : `${pct} sur toutes les SKILLCARDS`;
}
